import { ApiError } from '@packages/api';

/**
 * True when the error is a session-expired 401. Both the pre-flight expiry
 * check and the 401-response handler throw `ApiError(401, 'UNAUTHORIZED',
 * 'Session expired')`, so a single status check covers both. Callers use this
 * to skip noisy reporting (toasts and Sentry) - the API client already
 * triggered the login redirect.
 */
export function isSessionExpiredError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

/**
 * True when Sentry should hear about the error. A 4xx the server returned on
 * purpose (a refused delete, a missing record) is a domain outcome the user is
 * already told about by toast or inline message, and the backend's own Sentry
 * filter drops the same class. A `VALIDATION_ERROR` stays reportable: a current
 * bundle sending a malformed request is a frontend bug.
 */
export function shouldReportToSentry(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  if (error.code === 'VALIDATION_ERROR') return true;
  return error.status < 400 || error.status >= 500;
}

/**
 * True when the error is the AI usage cap being hit (429 USAGE_LIMIT_EXCEEDED).
 * The plan cap is not a fault the user can retry past, so surfaces branch on
 * this to offer a warning plus an upgrade route rather than a red error state.
 * Callers must have preserved the `code` off the wire - a plain `Error` carrying
 * only the message reads as an ordinary failure here, which is the bug this
 * predicate exists to make visible.
 */
export function isUsageLimitError(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'USAGE_LIMIT_EXCEEDED';
}

/**
 * True when the chat server answers a connect outcome it already stored (409
 * CONNECT_OUTCOME_ALREADY_RECORDED). Nothing is wrong: the earlier request's row is
 * the record, so the caller re-reads the thread instead of reporting an error.
 */
export function isConnectOutcomeAlreadyRecordedError(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'CONNECT_OUTCOME_ALREADY_RECORDED';
}

/** The phone-card counterpart of {@link isConnectOutcomeAlreadyRecordedError} (409 PHONE_OUTCOME_ALREADY_RECORDED). */
export function isPhoneOutcomeAlreadyRecordedError(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'PHONE_OUTCOME_ALREADY_RECORDED';
}

/**
 * Maps an unknown error to a user-friendly toast payload.
 *
 * Rules:
 *  - 401 (UNAUTHORIZED): Handled by API client redirect - never toast.
 *  - 4xx validation/conflict: Show the server message directly (it's user-safe).
 *  - 429 (RATE_LIMITED): Special message asking user to wait.
 *  - 5xx: Generic "server error" message - server message may leak internals.
 *  - Network errors: "Could not reach the server".
 *  - Unknown: Generic fallback.
 */
export function toErrorToast(error: unknown): {
  variant: 'error' | 'warning';
  title: string;
  description?: string;
} | null {
  // 401 is handled by the API client (redirect to /login) - don't toast
  if (isSessionExpiredError(error)) return null;

  if (error instanceof ApiError) {

    // 402 - subscription required. Dispatch event for banner, show warning toast.
    if (error.status === 402) {
      return {
        variant: 'warning' as const,
        title: 'Subscription required',
        description: 'Subscribe to a plan to create or edit data.',
      };
    }

    // Email mismatch on invite acceptance - the invite page shows dedicated inline UI
    // with a retry button. This is an expected user journey, not an error.
    if (error.code === 'INVITE_EMAIL_MISMATCH') return null;

    // Rate limited
    if (error.code === 'RATE_LIMITED') {
      return {
        variant: 'warning',
        title: 'Too many requests',
        description: 'Please wait a moment and try again.',
      };
    }

    // Usage limit exceeded
    if (isUsageLimitError(error)) {
      return {
        variant: 'warning',
        title: 'Usage limit reached',
        description: error.message,
      };
    }

    // Client errors (4xx) - server message is user-safe
    if (error.status >= 400 && error.status < 500) {
      return {
        variant: 'error',
        title: 'Request failed',
        description: error.message,
      };
    }

    // Server errors (5xx) - don't expose internal details
    if (error.status >= 500) {
      return {
        variant: 'error',
        title: 'Server error',
        description: 'Something went wrong on our end. Please try again shortly.',
      };
    }

    // Parse errors or other ApiError variants
    return {
      variant: 'error',
      title: 'Something went wrong',
      description: error.message,
    };
  }

  // Network error (fetch throws TypeError on network failure)
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return {
      variant: 'error',
      title: 'Connection failed',
      description: 'Could not reach the server. Check your internet connection.',
    };
  }

  // Generic Error
  if (error instanceof Error) {
    return {
      variant: 'error',
      title: 'Something went wrong',
      description: error.message,
    };
  }

  return {
    variant: 'error',
    title: 'An unexpected error occurred',
  };
}

/**
 * Maps an unknown error to a single-line, user-safe message for inline display
 * next to the control that triggered it (as opposed to a toast).
 *
 * Rules:
 *  - 5xx ApiError: generalised so we never leak server internals.
 *  - Any other thrown Error: surface its message (4xx server messages are user-safe).
 *  - Anything else, or an Error with no message: the caller-supplied `fallback`.
 *
 * The blank-message case is real, not defensive: `parseErrorResponse` falls back
 * to `response.statusText` when the body is not a parseable error envelope, and
 * browsers report an empty statusText over HTTP/2. Without the guard that
 * renders as an error box containing nothing.
 */
export function inlineErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.status >= 500) {
    return 'Something went wrong on our end. Try again.';
  }
  if (error instanceof Error && error.message.trim() !== '') return error.message;
  return fallback;
}
